import { Component, DestroyRef, effect, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PlayerMessageService } from '../../services/player.service';
import { PixlEditorComponent } from '@wdidev/pixl-editor';
import { Store } from '@ngxs/store';
import { AuthState } from '../../authentication/auth.state';
import { environment } from '../../../../environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AngularSplitModule } from 'angular-split';
import { Config, Correct, ExerciseSubKind, IFrameMessage, LearnItem, LearnItemPart, Pages, PlayerContentMessage, PlayerSettings } from '../../types/player-types';
import { initialConfig, initialLearnItem, PARTS_BY_KIND } from '../../consts/player-consts';
import TurndownService from 'turndown';
import { Approval } from '../../types';
import { TranslationService } from '../../services/translationService';
import { getKind } from '../../util';
import { SpinnerComponent } from '../../spinner/spinner/spinner.component';
import { TranslocoModule } from '@jsverse/transloco';
import { ExamEntity } from '../../../lesson/exam/exam.entity';
import { ExerciseEntity } from '../../../lesson/exercise/exercise.entity';
import { ExerciseEntityService } from '../../../lesson/exercise/services/exercise.entity.service';
import { ExamEntityService } from '../../../lesson/exam/services/exam.entity.service';
import { from, Observable, switchMap, tap } from 'rxjs';

export interface ContentEntityDialogData {
  type: 'exam' | 'exercise'
  model: ExamEntity | ExerciseEntity
}

@Component({
  selector: 'pixl-exam-entity-view',
  standalone: true,
  imports: [
    MatDividerModule,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatDialogTitle,
    PixlEditorComponent,
    AngularSplitModule,
    SpinnerComponent,
    TranslocoModule
  ],
  templateUrl: './content-entity.component.html',
  styleUrl: './content-entity.component.scss'
})
export class ContentEntityComponent implements OnInit, OnDestroy {
  @ViewChild('playerPreviewIframe') playerPreviewIframe!: ElementRef<HTMLIFrameElement>;
  @ViewChild(MatDialogContent, { read: ElementRef }) dialogContent!: ElementRef<HTMLElement>;

  private readonly dialogRef = inject(MatDialogRef<ContentEntityComponent>);
  private sanitizer = inject(DomSanitizer);
  private store = inject(Store);
  private auth$ = this.store.select(AuthState.getToken);
  private exam$!: Observable<ExamEntity[]>;
  private exercise$!: Observable<ExerciseEntity[]>;
  private examcise!: ExamEntity | ExerciseEntity;
  private destroyRef = inject(DestroyRef);
  private turndownService = new TurndownService();
  private translationService = inject(TranslationService);
  private examService = inject(ExamEntityService);
  private exerciseService = inject(ExerciseEntityService);
  private isPlayerMessageReady = signal(false);

  public playerService = inject(PlayerMessageService);
  public editorVisible = signal(false);
  public dialogData = inject<ContentEntityDialogData>(MAT_DIALOG_DATA);
  public data: ExamEntity | ExerciseEntity = this.dialogData.model;
  public iframePreviewUrl: SafeResourceUrl | null = null;
  public explanationContentHtml = '';
  public authToken: string | null = '';
  public assetsUrl: string = environment.apiUrl + '/assets';
  public mediaUrl: string = environment.assetsHost + '/';
  public textPartVisible = signal(false);
  public enumerationPartVisible = signal(false);
  public enumPlaceholderPartVisible = signal(false);
  public taskName = signal('');
  public showAddExerciseButton = signal(false);
  public playerLoadError = signal(false);
  public accounting = signal(false);
  public playerContentMessage: PlayerContentMessage = {
    config: initialConfig,
    editable: true,
    kind: 'Examcise',
    learnItem: initialLearnItem,
  };

  constructor() {
    this.playerService.messages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(message => this.handlePlayerEvents(message));

    effect(() => {
      const textVisible = this.textPartVisible();
      const enumVisible = this.enumerationPartVisible();
      const placeholderVisible = this.enumPlaceholderPartVisible();

      if (!textVisible && !enumVisible && !placeholderVisible) {
        this.playerService.postMessageToPlayer(this.playerService.iFramePlayerPreviewUrl(this.data.consumerId), 'MarkdownEditorClosed');
      }
    });
  }

  ngOnInit(): void {
    let taskType = this.data.kind;
    if (this.data.config && this.data.config.exerciseSubKind) {
      taskType += `.${this.data.config.exerciseSubKind}`;
    }
    this.taskName.set(getKind(this.translationService, taskType));
    if (taskType === 'buchungstrainer') {
      this.accounting.set(true);
    }

    this.iframePreviewUrl = this.sanitizer
      .bypassSecurityTrustResourceUrl(this.playerService.iFramePlayerPreviewUrl(this.data.consumerId));

    this.auth$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((token: unknown) => {
        this.authToken = token as string | null;
      });

    const query: Record<string, string | number> = {
      'include': 'consumers,subjects',
      'filter[id]': this.data.id
    };

    switch (this.dialogData.type) {
      case 'exam':
        this.exam$ = from(
          this.examService.ensureLoaded(query)
        ).pipe(
          switchMap(() =>
            this.store.select(this.examService.allByQuery(query))
          ),
          tap(val => {
            this.examcise = val[0];
            if (this.examcise) {
              this.initMessage(this.examcise);
            }
          })
        );

        this.exam$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        break;
      case 'exercise':
        this.exercise$ = from(
          this.exerciseService.ensureLoaded(query)
        ).pipe(
          switchMap(() =>
            this.store.select(this.exerciseService.allByQuery(query))
          ),
          tap(val => {
            this.examcise = val[0];
            if (this.examcise) {
              this.initMessage(this.examcise);
            }
          })
        );

        this.exercise$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        break;
      default:
        console.error('::==> Unbekannter Dialogdatentyp <==::');
    }

    setTimeout(() => {
      if (!this.playerService.iFrameReady) {
        this.playerLoadError.set(true);
      }
    }, 10000);
  }

  ngOnDestroy(): void {
    this.playerService.pageIndex = -1;
    this.playerService.index = -1;
    this.playerService.iFrameReady = false;
  }

  private initMessage(examcise: ExamEntity | ExerciseEntity): void {
    const kindKey = (examcise.config && examcise.config.exerciseSubKind) ?
      examcise.config.exerciseSubKind :
      examcise.kind;

    const defaultPartsForKind = PARTS_BY_KIND[kindKey] || PARTS_BY_KIND[examcise.kind];

    if (!defaultPartsForKind) {
      alert(`Fehler: Der Aufgabentyp "${kindKey}" ist nicht definiert.`);
      this.onCancel();
      return;
    }

    let mappedPages: Pages[] = [];
    const rawPages = (examcise.pages as unknown) as Record<string, unknown>[] | null;

    if (rawPages && rawPages.length > 0) {
      mappedPages = rawPages.map((page): Pages => {
        const rawParts = (page.parts as unknown) as Record<string, unknown>[] | null;

        return {
          _t: (page._t as Pages['_t']) || 'SimpleLearnItemPage',
          showMarginalColumnOnMobile: Boolean(page.showMarginalColumnOnMobile),
          baseLayout: (page.baseLayout as string) ?? 'default',
          layout: (page.layout as string) || 'horizontalLayout',
          parts: (rawParts && rawParts.length > 0) ?
            rawParts.map((part): LearnItemPart => {
              const { _t, ...rest } = part;
              return {
                ...rest,
                _t: _t as string,
              } as LearnItemPart;
            }) :
            defaultPartsForKind
        };
      });
    }
    else {
      mappedPages = [
        {
          _t: 'SimpleLearnItemPage',
          showMarginalColumnOnMobile: false,
          baseLayout: 'default',
          layout: 'horizontalLayout',
          parts: defaultPartsForKind
        }
      ];
    }

    const mappedConfig: Config = {
      feedback: [],
      kiService: false,
      playerSettings: null,
      resolutions: [1200, 800, 600, 100],
      sessionTimeoutMinutes: { value: 60 },
      // Cast auf den Union-Type
      exerciseSubKind: examcise.config?.exerciseSubKind as ExerciseSubKind
    };

    const mappedLernItem: LearnItem = {
      id: examcise.id,
      subjectId: examcise.subjectId,
      consumerId: examcise.consumerId,
      name: examcise.name,
      kind: examcise.kind,
      assignments: (examcise.assignments as unknown[]) || [],
      assets: examcise.assets,
      pages: mappedPages,
      correct: examcise.correct ? (examcise.correct as unknown as Correct[]) : [],
      config: mappedConfig,
      copyRestricted: Boolean(examcise.copyRestricted),
      consumerData: examcise.consumerData || {},
      approval: examcise.approval as Approval,
      updated: examcise.updated ? new Date(examcise.updated) : new Date(),
      changedBy: examcise.changedBy || '',
      lastVersion: examcise.lastVersion || '1.0',
      version: examcise.version || 1,
      dateCreated: new Date().toISOString(),
      wrongThings: [],
      isExam: this.dialogData.type === 'exam'
    };

    this.playerContentMessage.learnItem = mappedLernItem;

    let loadingCount = 2;
    const checkAndReady = () => {
      loadingCount--;
      if (loadingCount === 0) {
        this.isPlayerMessageReady.set(true);
        if (this.playerService.iFrameReady) {
          this.postMessage2Player();
        }
      }
    };

    this.playerService.getPlayerSettings$(examcise.consumerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => {
          this.playerContentMessage.config.playerSettings = settings as PlayerSettings;
          checkAndReady();
        },
        error: () => checkAndReady()
      });

    this.playerService.getConsumer$(examcise.consumerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (consumer) => {
          this.playerContentMessage.config.kiService = Boolean(consumer?.kiService);
          checkAndReady();
        },
        error: () => checkAndReady()
      });
  }

  private handlePlayerEvents(message: IFrameMessage): void {
    if (message.kind === 'MessageHubInitialized') {
      if (this.isPlayerMessageReady()) {
        console.log('::==> Player Ready empfangen. Warte 1,5s vor postMessage... <==::');
        setTimeout(() => {
          this.playerService.iFrameReady = true;
          console.log('::==> Pause vorbei, sende Initial-Daten an Player <==::');
          this.postMessage2Player();
        }, 1500);
      }
      return;
    }

    if (message.kind === 'EditPart') {
      this.playerService.pageIndex = message.pageIndex;
      this.playerService.index = message.index;
      const part = this.playerContentMessage.learnItem.pages[this.playerService.pageIndex].parts[this.playerService.index];

      if (part) {
        this.textPartVisible.set(false);
        this.enumerationPartVisible.set(false);
        this.enumPlaceholderPartVisible.set(false);

        switch (part._t) {
          case 'TextPart':
            this.explanationContentHtml = part.markup || '';
            this.showAddExerciseButton.set(false);
            setTimeout(() => this.textPartVisible.set(true), 0);
            break;
          case 'EnumerationPart':
            this.explanationContentHtml = part.text;
            this.showAddExerciseButton.set(true);
            setTimeout(() => this.enumerationPartVisible.set(true), 0);
            break;
          case 'EnumPlaceholderPart':
            this.explanationContentHtml = part.text || '';
            this.showAddExerciseButton.set(true);
            setTimeout(() => this.enumPlaceholderPartVisible.set(true), 0);
            break;
          default:
            alert(`Unsupported _t: ${part._t}`);
            break;
        }
      }
    }
  }

  private postMessage2Player(): void {
    if (!this.isPlayerMessageReady()) return;
    this.playerService.postMessageToPlayer(this.playerService.iFramePlayerPreviewUrl(this.data.consumerId), this.playerContentMessage);
  }

  public getLeftAreaSize(): number {
    return (this.textPartVisible() || this.enumerationPartVisible() || this.enumPlaceholderPartVisible()) ? 50 : 100;
  }

  public getLeftAreaMinSize(): number {
    return (this.textPartVisible() || this.enumerationPartVisible() || this.enumPlaceholderPartVisible()) ? 5 : 0;
  }

  public onIFrameLoaded(): void {
    if (this.playerPreviewIframe && this.playerPreviewIframe.nativeElement.contentWindow) {
      this.playerService.iFrameReady = true;
      this.playerService.playerPreviewIFrame = this.playerPreviewIframe;
      if (this.isPlayerMessageReady()) {
        this.postMessage2Player();
      }
    }
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onSave(): void {
    const pagesForBackend = this.playerContentMessage.learnItem.pages.map((page: Pages) => {
      const { _t, parts, ...rest } = page;
      return {
        ...rest,
        _t: _t,
        parts: parts.map((part: LearnItemPart) => {
          const { _t: partClass, ...partRest } = part;
          return { ...partRest, _t: partClass };
        })
      };
    });

    const updatedModel = {
      ...this.dialogData.model,
      pages: pagesForBackend as unknown as string[]
    };

    this.dialogRef.close(updatedModel);
  }

  public callbackFct(ev: string): void {
    this.explanationContentHtml = ev;
    const currentPart = this.playerContentMessage.learnItem.pages[this.playerService.pageIndex].parts[this.playerService.index];

    if (currentPart) {
      if (currentPart._t === 'TextPart') {
        currentPart.markup = this.explanationContentHtml;
        currentPart.markdown = this.turndownService.turndown(this.explanationContentHtml);
      }
      else if (currentPart._t === 'EnumerationPart' || currentPart._t === 'EnumPlaceholderPart') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (currentPart as any).text = this.explanationContentHtml;
      }
    }
    this.postMessage2Player();
  }
}
